import { Component, Inject, OnInit, OnDestroy, EventEmitter, Output, ChangeDetectionStrategy } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subject, Observable, of, merge } from 'rxjs';
import { takeUntil, map, startWith } from 'rxjs/operators';
import { OrderParkingVM, OrderShuttleVM } from '../../../../../models/orderModel';
import { ArrivalVM, DepartureVM } from '../../../../../models/ArrivalDepartureModel';
import { BackOfficeService } from '../../../../../services/backoffice.service';
import { ArrivalDepartureService } from '../../../../../services/arrivaldeparture.service';
import { OrderShuttleService } from '../../../../../services/ordershuttle.service';
import { CartService } from '../../../../../services/cart.service';
import moment from 'moment';
import { format } from 'date-fns';

@Component({
  selector: 'app-back-office24-order-parking-edit-time',
  templateUrl: './back-office24-order-parking-edit-time.component.html',
  styleUrls: ['./back-office24-order-parking-edit-time.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class BackOffice24OrderParkingEditTimeComponent implements OnInit, OnDestroy {
  @Output() saveChangesEvent = new EventEmitter<void>();
  editOrderParkingTimeForm: UntypedFormGroup;
  orderParkingVM: OrderParkingVM;
  orderShuttleDepartureVM: OrderShuttleVM;
  orderShuttleArrivalVM: OrderShuttleVM;
  componentDestroyed$: Subject<boolean> = new Subject();
  isLoading = false;
  activeRequests = 0;
  isPrefilling = false;
  additionalPaymentAmount: number = 0;
  overbooking: boolean = false;
  lastQueriedParkingFrom = '';
  lastQueriedParkingTo = '';

  departures: DepartureVM[] = [];
  arrivals: ArrivalVM[] = [];
  refreshDepartures$: Subject<void> = new Subject<void>();
  refreshArrivals$: Subject<void> = new Subject<void>();
  filteredDepartures: Observable<DepartureVM[]>;
  filteredArrivals: Observable<ArrivalVM[]>;

  departureArrivalTimeOptions: { label: string, value: string }[] = [];

  terminalOptions: { value: string; name: string }[] = [];

  selectedFlightStationId = '5ac25e00-7863-4b8c-9fe1-eb8af7c7e77e';
  readonly skavstaOrgId = 'DC3E5C36-A4F7-4886-98F2-4833EA27F84B';

  constructor(
    @Inject(MAT_DIALOG_DATA) public orderParkingId: string,
    private dialogRef: MatDialogRef<BackOffice24OrderParkingEditTimeComponent>,
    private fb: UntypedFormBuilder,
    private snackBar: MatSnackBar,
    private backOfficeService: BackOfficeService,
    private arrivalDepartureService: ArrivalDepartureService,
    private orderShuttleService: OrderShuttleService,
    private cartService: CartService
  ) { }

  ngOnInit() {
    this.initForm();
    this.getTerminalOptions();
    if (this.orderParkingId) {
      this.loadData(this.orderParkingId);
    }
  }

  getTerminalOptions() {
    this.arrivalDepartureService
      .getTerminals('59D90114-9889-474E-A8A8-D1926F0DEE70')
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(
        terminals => {
          this.terminalOptions = (terminals || []).map(t => ({
            value: t.terminalName,
            name: t.terminalName
          }));
          // this.ensureSelectedTerminalOptions();
        },
        error => {
          console.error('Error fetching terminals:', error);
        }
      );
  }

  // ensureSelectedTerminalOptions() {
  //   const depTerm = this.orderShuttleDepartureVM?.terminal || this.editOrderParkingTimeForm?.get('departureShuttleToTerminal')?.value;
  //   if (depTerm && !this.terminalOptions.some(opt => opt.value === depTerm)) {
  //     this.terminalOptions.push({ value: depTerm, name: depTerm });
  //   }
  //   const arrTerm = this.orderShuttleArrivalVM?.terminal || this.editOrderParkingTimeForm?.get('arrivalShuttleFromTerminal')?.value;
  //   if (arrTerm && !this.terminalOptions.some(opt => opt.value === arrTerm)) {
  //     this.terminalOptions.push({ value: arrTerm, name: arrTerm });
  //   }
  // }

  private incrementRequests() {
    this.activeRequests++;
    this.isLoading = true;
  }

  private decrementRequests() {
    if (this.activeRequests > 0) this.activeRequests--;
    if (this.activeRequests === 0) this.isLoading = false;
  }

  initForm() {
    this.editOrderParkingTimeForm = this.fb.group({
      departureType: ['flight'],
      departureDate: [''],
      departureFlight: [''],
      departureArrivalTimeToParking: [''],

      departureShuttleToTerminal: [''],
      departureShuttleDate: [''],
      departureShuttleTime: [''],

      departurePassengers: [1],
      departureBaggage: ['no'],

      arrivalType: ['flight'],
      arrivalDate: [''],
      arrivalFlight: [''],

      arrivalShuttleFromTerminal: [''],
      arrivalShuttleDate: [''],
      arrivalShuttleTime: [''],

      arrivalPassengers: [1],
      arrivalBaggage: ['no'],

      createPayment: ['no']
    });

    this.filteredDepartures = merge(
      this.editOrderParkingTimeForm.get('departureFlight').valueChanges,
      this.refreshDepartures$.pipe(map(() => this.editOrderParkingTimeForm.get('departureFlight').value))
    ).pipe(
      startWith(''),
      map(value => typeof value === 'string' ? value : value?.departureCode),
      map(name => name ? this._filterDepartures(name) : this.departures.slice())
    );

    this.filteredArrivals = merge(
      this.editOrderParkingTimeForm.get('arrivalFlight').valueChanges,
      this.refreshArrivals$.pipe(map(() => this.editOrderParkingTimeForm.get('arrivalFlight').value))
    ).pipe(
      startWith(''),
      map(value => typeof value === 'string' ? value : value?.arrivalCode),
      map(name => name ? this._filterArrivals(name) : this.arrivals.slice())
    );

    merge(
      this.editOrderParkingTimeForm.get('departureArrivalTimeToParking').valueChanges,
      this.editOrderParkingTimeForm.get('arrivalFlight').valueChanges,
      this.editOrderParkingTimeForm.get('departureShuttleDate').valueChanges,
      this.editOrderParkingTimeForm.get('departureShuttleTime').valueChanges,
      this.editOrderParkingTimeForm.get('arrivalShuttleDate').valueChanges,
      this.editOrderParkingTimeForm.get('arrivalShuttleTime').valueChanges
    ).pipe(
      takeUntil(this.componentDestroyed$)
    ).subscribe(() => {
      setTimeout(() => {
        if (!this.isLoading) {
          this.getAvailableParkingsForOrganization();
        }
      });
    });
  }

  /** Loads parking info then loads shuttles for that order */
  loadData(orderParkingId: string) {
    this.incrementRequests();
    this.backOfficeService.getOrderParking(orderParkingId).pipe(takeUntil(this.componentDestroyed$)).subscribe(
      orderParking => {
        this.orderParkingVM = orderParking;
        this.decrementRequests();
        this.loadShuttles(orderParking.orderId);
      },
      () => this.decrementRequests()
    );
  }

  /** Loads the shuttles for the order and prefills the form */
  loadShuttles(orderId: string) {
    this.incrementRequests();
    this.orderShuttleService.getOrderShuttleOrderId(orderId).pipe(takeUntil(this.componentDestroyed$)).subscribe(
      shuttles => {
        this.decrementRequests();
        shuttles.forEach(shuttle => {
          if (shuttle.productType === 'ShuttleDeparture') {
            this.orderShuttleDepartureVM = shuttle;
          }
          if (shuttle.productType === 'ShuttleArrival') {
            this.orderShuttleArrivalVM = shuttle;
          }
        });
        this.prefillForm();
      },
      () => this.decrementRequests()
    );
  }

  /** Prefill all form fields from loaded parking + shuttle data */
  prefillForm() {
    this.isPrefilling = true;
    const parking = this.orderParkingVM;
    const dep = this.orderShuttleDepartureVM;
    const arr = this.orderShuttleArrivalVM;

    // --- Departure ---
    if (dep?.departureId) {
      // Flight mode
      this.editOrderParkingTimeForm.patchValue({ departureType: 'flight' });
      this.editOrderParkingTimeForm.patchValue({
        departurePassengers: dep.passengers || parking?.passengers || 1,
        departureBaggage: dep.baggage > 0 ? 'yes' : 'no'
      });
      // Load flight list then set selected flight
      this.incrementRequests();
      this.arrivalDepartureService.getDeparture(dep.departureId).pipe(takeUntil(this.componentDestroyed$)).subscribe(
        departureVM => {
          this.decrementRequests();
          const stationId = departureVM.stationId || this.selectedFlightStationId;
          const flightDate = moment(departureVM.scheduledDeparture);
          this.editOrderParkingTimeForm.patchValue({ departureDate: flightDate.toDate() });
          this.incrementRequests();
          this.arrivalDepartureService.getDeparturesStationBetweenDepartureDate(
            stationId, flightDate.format('YYYY-MM-DD'), flightDate.add(1, 'd').format('YYYY-MM-DD')
          ).pipe(takeUntil(this.componentDestroyed$)).subscribe(
            depList => {
              this.decrementRequests();
              this.departures = depList || [];
              this.refreshDepartures$.next();
              const matched = this.departures.find(d => d.departureId === dep.departureId);
              if (matched) {
                this.editOrderParkingTimeForm.patchValue({ departureFlight: matched }, { emitEvent: false });
                this.departureFlightSelected(matched, true);
              }
            },
            () => this.decrementRequests()
          );
        },
        () => this.decrementRequests()
      );
    } else if (dep) {
      // Hotel mode
      const shuttleDate = moment(dep.shuttleDate);
      this.editOrderParkingTimeForm.patchValue({
        departureType: 'hotel',
        departureShuttleToTerminal: dep.terminal || '',
        departureShuttleDate: shuttleDate.toDate(),
        departureShuttleTime: shuttleDate.format('HH:mm'),
        departurePassengers: dep.passengers || parking?.passengers || 1,
        departureBaggage: dep.baggage > 0 ? 'yes' : 'no'
      });
    } else {
      // Fallback from parking dates
      if (parking?.parkingFrom) {
        const pFrom = moment(parking.parkingFrom);
        this.editOrderParkingTimeForm.patchValue({
          departureType: 'hotel',
          departureShuttleDate: pFrom.toDate(),
          departureShuttleTime: pFrom.format('HH:mm'),
          departurePassengers: parking.passengers || 1
        });
      }
    }

    // --- Arrival ---
    if (arr?.arrivalId) {
      // Flight mode
      this.editOrderParkingTimeForm.patchValue({ arrivalType: 'flight' });
      this.editOrderParkingTimeForm.patchValue({
        arrivalPassengers: arr.passengers || parking?.passengers || 1,
        arrivalBaggage: arr.baggage > 0 ? 'yes' : 'no'
      });
      // Load flight list then set selected flight
      this.incrementRequests();
      this.arrivalDepartureService.getArrival(arr.arrivalId).pipe(takeUntil(this.componentDestroyed$)).subscribe(
        arrivalVM => {
          this.decrementRequests();
          const stationId = arrivalVM.stationId || this.selectedFlightStationId;
          const flightDate = moment(arrivalVM.scheduledArrival);
          this.editOrderParkingTimeForm.patchValue({ arrivalDate: flightDate.toDate() });
          this.incrementRequests();
          this.arrivalDepartureService.getArrivalsStationBetweenArrivalDate(
            stationId, flightDate.format('YYYY-MM-DD'), flightDate.add(1, 'd').format('YYYY-MM-DD')
          ).pipe(takeUntil(this.componentDestroyed$)).subscribe(
            arrList => {
              this.decrementRequests();
              this.arrivals = arrList || [];
              this.refreshArrivals$.next();
              const matched = this.arrivals.find(a => a.arrivalId === arr.arrivalId);
              if (matched) {
                this.editOrderParkingTimeForm.patchValue({ arrivalFlight: matched }, { emitEvent: false });
              }
              setTimeout(() => this.isPrefilling = false);
            },
            () => { this.decrementRequests(); setTimeout(() => this.isPrefilling = false); }
          );
        },
        () => this.decrementRequests()
      );
    } else if (arr) {
      // Hotel mode
      const shuttleDate = moment(arr.shuttleDate);
      this.editOrderParkingTimeForm.patchValue({
        arrivalType: 'hotel',
        arrivalShuttleFromTerminal: arr.terminal || '',
        arrivalShuttleDate: shuttleDate.toDate(),
        arrivalShuttleTime: shuttleDate.format('HH:mm'),
        arrivalPassengers: arr.passengers || parking?.passengers || 1,
        arrivalBaggage: arr.baggage > 0 ? 'yes' : 'no'
      });
      setTimeout(() => this.isPrefilling = false);
    } else {
      // Fallback from parking dates
      if (parking?.parkingTo) {
        const pTo = moment(parking.parkingTo);
        this.editOrderParkingTimeForm.patchValue({
          arrivalType: 'hotel',
          arrivalShuttleDate: pTo.toDate(),
          arrivalShuttleTime: pTo.format('HH:mm'),
          arrivalPassengers: parking.passengers || 1
        });
        setTimeout(() => this.isPrefilling = false);
      } else {
        setTimeout(() => this.isPrefilling = false);
      }
    }

    // this.ensureSelectedTerminalOptions();
    // Initial fetch of additional payment amount
    setTimeout(() => {
      this.getAvailableParkingsForOrganization();
    }, 500);
  }

  async departureDateChanged() {
    const date = this.editOrderParkingTimeForm.get('departureDate').value;
    if (!date) return;
    const startDate = moment(date).format('YYYY-MM-DD');
    const endDate = moment(date).add(1, 'd').format('YYYY-MM-DD');
    this.incrementRequests();
    try {
      this.departures = await this.arrivalDepartureService.getDeparturesStationBetweenDepartureDate(this.selectedFlightStationId, startDate, endDate).toPromise();
      if (!this.departures) this.departures = [];
      this.refreshDepartures$.next();

      this.editOrderParkingTimeForm.patchValue({
        departureFlight: '',
        departureArrivalTimeToParking: ''
      });
    } catch (e) {
      this.departures = [];
      this.refreshDepartures$.next();
    } finally {
      this.decrementRequests();
    }
  }

  async arrivalDateChanged() {
    const date = this.editOrderParkingTimeForm.get('arrivalDate').value;
    if (!date) return;
    const startDate = moment(date).format('YYYY-MM-DD');
    const endDate = moment(date).add(1, 'd').format('YYYY-MM-DD');
    this.incrementRequests();
    try {
      this.arrivals = await this.arrivalDepartureService.getArrivalsStationBetweenArrivalDate(this.selectedFlightStationId, startDate, endDate).toPromise();
      if (!this.arrivals) this.arrivals = [];
      this.refreshArrivals$.next();

      this.editOrderParkingTimeForm.patchValue({
        arrivalFlight: ''
      });
    } catch (e) {
      this.arrivals = [];
      this.refreshArrivals$.next();
    } finally {
      this.decrementRequests();
    }
  }

  /** Called when user selects a departure flight from autocomplete. prefilled=true skips parkingFrom override */
  departureFlightSelected(departure: DepartureVM, prefilled = false) {
    if (!departure) return;
    const flightTime = moment(departure.scheduledDeparture);
    this.departureArrivalTimeOptions = [
      { label: `4.5h before KL: ${flightTime.clone().subtract(270, 'm').format('HH:mm')}`, value: flightTime.clone().subtract(270, 'm').toISOString() },
      { label: `4h before KL: ${flightTime.clone().subtract(240, 'm').format('HH:mm')}`, value: flightTime.clone().subtract(240, 'm').toISOString() },
      { label: `3.5h before KL: ${flightTime.clone().subtract(210, 'm').format('HH:mm')}`, value: flightTime.clone().subtract(210, 'm').toISOString() },
      { label: `3h before KL: ${flightTime.clone().subtract(180, 'm').format('HH:mm')}`, value: flightTime.clone().subtract(180, 'm').toISOString() },
      { label: `2.5h before KL: ${flightTime.clone().subtract(150, 'm').format('HH:mm')}`, value: flightTime.clone().subtract(150, 'm').toISOString() },
      { label: `2h before KL: ${flightTime.clone().subtract(120, 'm').format('HH:mm')}`, value: flightTime.clone().subtract(120, 'm').toISOString() },
      { label: `1.5h before KL: ${flightTime.clone().subtract(90, 'm').format('HH:mm')}`, value: flightTime.clone().subtract(90, 'm').toISOString() },
      { label: `1h before KL: ${flightTime.clone().subtract(60, 'm').format('HH:mm')}`, value: flightTime.clone().subtract(60, 'm').toISOString() }
    ];

    // If prefilling from existing data, match parking.parkingFrom to closest option
    if (prefilled && this.orderParkingVM?.parkingFrom) {
      const existingFrom = moment(this.orderParkingVM.parkingFrom);
      const closestOpt = this.departureArrivalTimeOptions.reduce((prev, curr) =>
        Math.abs(moment(curr.value).diff(existingFrom)) < Math.abs(moment(prev.value).diff(existingFrom)) ? curr : prev
      );
      this.editOrderParkingTimeForm.patchValue({ departureArrivalTimeToParking: closestOpt.value });
    } else {
      this.editOrderParkingTimeForm.patchValue({ departureArrivalTimeToParking: this.departureArrivalTimeOptions[3]?.value });
    }
  }
  get isSkavsta(): boolean {
    return this.orderParkingVM?.organizationId?.toLowerCase() === this.skavstaOrgId.toLowerCase();
  }

  get computedParkingFrom(): Date | null {
    const value = this.editOrderParkingTimeForm?.value;
    if (!value) return null;
    if (value.departureType === 'flight') {
      return value.departureArrivalTimeToParking ? new Date(value.departureArrivalTimeToParking) : null;
    } else {
      if (!value.departureShuttleDate || !value.departureShuttleTime) return null;
      const date = moment(value.departureShuttleDate).format('YYYY-MM-DD');
      return new Date(`${date}T${value.departureShuttleTime}`);
    }
  }

  get computedParkingTo(): Date | null {
    const value = this.editOrderParkingTimeForm?.value;
    if (!value) return null;
    if (value.arrivalType === 'flight') {
      const arrFlight = value.arrivalFlight;
      if (!arrFlight?.scheduledArrival) return null;
      return moment(arrFlight.scheduledArrival).add(1, 'hour').toDate();
    } else {
      if (!value.arrivalShuttleDate || !value.arrivalShuttleTime) return null;
      const date = moment(value.arrivalShuttleDate).format('YYYY-MM-DD');
      const base = moment(`${date}T${value.arrivalShuttleTime}`);
      return base.toDate();
    }
  }

  // ---- Filters ----

  private _filterDepartures(value: string): DepartureVM[] {
    const filterValue = value.toLowerCase();
    return this.departures.filter(dep => this.displayDepartureFn(dep).toLowerCase().includes(filterValue));
  }

  private _filterArrivals(value: string): ArrivalVM[] {
    const filterValue = value.toLowerCase();
    return this.arrivals.filter(arr => this.displayArrivalFn(arr).toLowerCase().includes(filterValue));
  }

  displayDepartureFn(departure: DepartureVM): string {
    if (!departure || !departure.scheduledDeparture) return '';
    try {
      const time = format(new Date(departure.scheduledDeparture), 'HH:mm');
      return `${departure.departureCode} ${departure.arrivalStation} : ${time} ${departure.stationCode} : ${departure.terminalName}`;
    } catch (e) { return ''; }
  }

  displayArrivalFn(arrival: ArrivalVM): string {
    if (!arrival || !arrival.scheduledArrival) return '';
    try {
      const time = format(new Date(arrival.scheduledArrival), 'HH:mm');
      return `${arrival.arrivalCode} ${arrival.departureStation} : ${time} ${arrival.stationCode} : ${arrival.terminalName}`;
    } catch (e) { return ''; }
  }

  // ---- Save ----

  getAvailableParkingsForOrganization() {
    if (!this.orderParkingVM?.orderId || !this.computedParkingFrom || !this.computedParkingTo) {
      return;
    }
    const periodFrom = moment(this.computedParkingFrom).format('YYYY-MM-DDTHH:mm:ss');
    const periodTo = moment(this.computedParkingTo).format('YYYY-MM-DDTHH:mm:ss');

    if (this.lastQueriedParkingFrom === periodFrom && this.lastQueriedParkingTo === periodTo) {
      return;
    }

    this.lastQueriedParkingFrom = periodFrom;
    this.lastQueriedParkingTo = periodTo;

    this.cartService.getAvailableParkingsOnEdit(this.orderParkingVM.orderId, periodFrom, periodTo, 'NONE')
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((parkings: any) => {
        const parking = parkings?.parkings && parkings.parkings.length > 0 ? parkings.parkings[0] : null;
        if (parking) {
          const amount = (Number(parking.additionalPaymentAmount || 0)) * 1.25;
          this.additionalPaymentAmount = isNaN(amount) ? 0 : Number(amount.toFixed(2));
          this.overbooking = parking.availableParkingSpots <= 0 ? true : false;
        } else {
          this.additionalPaymentAmount = 0;
          this.overbooking = false;
        }
      }, err => {
        console.error(err);
      });
  }

  get isFormValid(): boolean {
    const v = this.editOrderParkingTimeForm?.value;
    if (!v) return false;

    // Departure Validation
    if (v.departureType === 'flight') {
      if (!v.departureDate || !v.departureFlight || !v.departureArrivalTimeToParking || !v.departurePassengers) {
        return false;
      }
    } else if (v.departureType === 'hotel') {
      if (!v.departureShuttleToTerminal || !v.departureShuttleDate || !v.departureShuttleTime || !v.departurePassengers) {
        return false;
      }
    }

    // Arrival Validation
    if (v.arrivalType === 'flight') {
      if (!v.arrivalDate || !v.arrivalFlight || !v.arrivalPassengers) {
        return false;
      }
    } else if (v.arrivalType === 'hotel') {
      if (!v.arrivalShuttleFromTerminal || !v.arrivalShuttleDate || !v.arrivalShuttleTime || !v.arrivalPassengers) {
        return false;
      }
    }

    return true;
  }

  saveChanges() {
    const value = this.editOrderParkingTimeForm.value;
    let parkingFrom = '';
    let parkingTo = '';
    const shuttles = [];

    if (value.departureType === 'flight') {
      const pFromDate = moment(value.departureArrivalTimeToParking);
      parkingFrom = pFromDate.format('YYYY-MM-DDTHH:mm:ss');
      const depFlight = value.departureFlight;
      shuttles.push({
        orderShuttleId: this.orderShuttleDepartureVM?.orderShuttleId,
        stationCode: depFlight?.stationCode || 'P1',
        shuttleTypeCode: this.orderShuttleDepartureVM?.shuttleTypeCode || 'STANDARD',
        productCode: 'SHUTDEP',
        shuttleDate: pFromDate.format('YYYY-MM-DDTHH:mm:ss'),
        departureId: depFlight?.departureId || null,
        flightNr: depFlight?.departureCode || null,
        flightDate: depFlight ? moment(depFlight.scheduledDeparture).format('YYYY-MM-DDTHH:mm:ss') : '',
        flightTerminal: depFlight?.terminalName || '',
        terminal: depFlight?.terminalName || '',
        passengers: value.departurePassengers?.toString(),
        childSeats: 0,
        baggage: value.departureBaggage === 'yes' ? 1 : 0,
        comment: ''
      });
    } else {
      const depDate = moment(value.departureShuttleDate).format('YYYY-MM-DD');
      parkingFrom = `${depDate}T${value.departureShuttleTime}:00`;
      shuttles.push({
        orderShuttleId: this.orderShuttleDepartureVM?.orderShuttleId,
        stationCode: 'P1',
        shuttleTypeCode: this.orderShuttleDepartureVM?.shuttleTypeCode || 'STANDARD',
        productCode: 'SHUTDEP',
        shuttleDate: parkingFrom,
        departureId: null,
        flightNr: null,
        flightDate: '',
        flightTerminal: '',
        terminal: value.departureShuttleToTerminal,
        passengers: value.departurePassengers?.toString(),
        childSeats: 0,
        baggage: value.departureBaggage === 'yes' ? 1 : 0,
        comment: ''
      });
    }

    if (value.arrivalType === 'flight') {
      const arrFlight = value.arrivalFlight;
      const baggageOffset = value.arrivalBaggage === 'yes' ? 45 : 25;
      const arrTime = arrFlight ? moment(arrFlight.scheduledArrival).add(baggageOffset, 'minutes') : moment();
      parkingTo = this.computedParkingTo ? moment(this.computedParkingTo).format('YYYY-MM-DDTHH:mm:ss') : arrTime.format('YYYY-MM-DDTHH:mm:ss');
      shuttles.push({
        orderShuttleId: this.orderShuttleArrivalVM?.orderShuttleId,
        stationCode: arrFlight?.stationCode || 'P1',
        shuttleTypeCode: this.orderShuttleArrivalVM?.shuttleTypeCode || 'STANDARD',
        productCode: 'SHUTARR',
        shuttleDate: arrTime.format('YYYY-MM-DDTHH:mm:ss'),
        arrivalId: arrFlight?.arrivalId || null,
        flightNr: arrFlight?.arrivalCode || null,
        flightDate: arrFlight ? moment(arrFlight.scheduledArrival).format('YYYY-MM-DDTHH:mm:ss') : '',
        flightTerminal: arrFlight?.terminalName || '',
        terminal: arrFlight?.terminalName || '',
        passengers: value.arrivalPassengers?.toString(),
        childSeats: 0,
        baggage: value.arrivalBaggage === 'yes' ? 1 : 0,
        comment: ''
      });
    } else {
      const arrDate = moment(value.arrivalShuttleDate).format('YYYY-MM-DD');
      const arrShuttleDateTime = `${arrDate}T${value.arrivalShuttleTime}:00`;
      parkingTo = this.computedParkingTo ? moment(this.computedParkingTo).format('YYYY-MM-DDTHH:mm:ss') : arrShuttleDateTime;
      shuttles.push({
        orderShuttleId: this.orderShuttleArrivalVM?.orderShuttleId,
        stationCode: 'P1',
        shuttleTypeCode: this.orderShuttleArrivalVM?.shuttleTypeCode || 'STANDARD',
        productCode: 'SHUTARR',
        shuttleDate: arrShuttleDateTime,
        arrivalId: null,
        flightNr: null,
        flightDate: '',
        flightTerminal: '',
        terminal: value.arrivalShuttleFromTerminal,
        passengers: value.arrivalPassengers?.toString(),
        childSeats: 0,
        baggage: value.arrivalBaggage === 'yes' ? 1 : 0,
        comment: ''
      });
    }

    const payload = {
      orderId: this.orderParkingVM?.orderId,
      parking: {
        carId: this.orderParkingVM?.carId,
        parkingFrom: parkingFrom,
        parkingTo: parkingTo,
        passengers: (Math.max(value.departurePassengers, value.arrivalPassengers))?.toString()
      },
      shuttles: shuttles,
      // shouldPay: value.createPayment === 'yes' ? true : false,
      shouldPay: this.additionalPaymentAmount > 0 ? true : false,
      useOverbooking: this.overbooking
    };

    this.isLoading = true;
    this.backOfficeService.updateOrderParkingAndShuttles(payload).subscribe(
      () => {
        this.snackBar.open('Saved changes successfully', 'OK', { duration: 2000, verticalPosition: 'top' });
        this.saveChangesEvent.emit();
        this.dialogRef.close(true);
      },
      (error) => {
        this.isLoading = false;
        this.snackBar.open(`Error: ${error.status}`, 'Close', { verticalPosition: 'top' });
      }
    );
  }

  selectDepartureFlight(departure: DepartureVM, trigger: any, event: Event) {
    event.preventDefault();
    event.stopPropagation();
    this.editOrderParkingTimeForm.get('departureFlight').setValue(departure, { emitEvent: true });
    this.departureFlightSelected(departure);
    trigger?.closePanel();
  }

  selectArrivalFlight(arrival: ArrivalVM, trigger: any, event: Event) {
    event.preventDefault();
    event.stopPropagation();
    this.editOrderParkingTimeForm.get('arrivalFlight').setValue(arrival, { emitEvent: true });
    trigger?.closePanel();
  }

  ngOnDestroy() {
    this.componentDestroyed$.next(true);
    this.componentDestroyed$.complete();
  }
}
